import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams, useSearchParams, useLocation } from "react-router-dom";
import { ArrowLeft, Check, Plus, Minus, ArrowRight, RefreshCw, Calendar, Clock, MapPin, User, ChevronRight, Wrench, Mail } from "lucide-react";
import { toast } from "sonner";
import { Celebration } from "../components/Celebration";
import { InviteSheet } from "../components/InviteSheet";
import RequirementDescription from "../components/RequirementDescription";
import { useCommons } from "../lib/store";
import { useTour } from "../tour/TourProvider";
import { makePledge, setQuantity, withdraw, declineInvite, launchProject, inviteToProject } from "../lib/api";
import {
  projectById, personById, peopleRequirements, thingRequirements,
  pledgedForReq, personIsIn, personPledges, totalPeopleCommitted, totalPeopleNeeded,
  projectProgress, commitmentsAreMutable, isStewardOrCo,
  labelForCount, uniqueHelpersCount, activePledges, inviterForProject,
  effectiveStatus, allRequirementsMet,
  mayPledgePeople, mayPledgeThing, mayInvite, hasPendingInvitation,
} from "../lib/domain";
import { statusLabelDynamic } from "../lib/theme";
import {
  isCruiseShipProject,
  cruiseTravelerHeadline,
  CRUISE_ID,
} from "../lib/cruiseDemo";

// -- helpers --------------------------------------------------------

function chainInfo(project, projects) {
  // Walk previousAttemptId backwards to find the original (Attempt 1)
  const chain = [project];
  let cur = project;
  while (cur.previousAttemptId) {
    const prev = projects.find((p) => p.id === cur.previousAttemptId);
    if (!prev) break;
    chain.unshift(prev);
    cur = prev;
  }
  return chain;
}

function fullChainInfo(project, projects) {
  // Walk both directions so the pager can jump to any attempt in the
  // story — earlier tries and future ones — with a single tap.
  const back = chainInfo(project, projects);
  const chain = [...back];
  let cur = project;
  while (cur.nextAttemptId) {
    const next = projects.find((p) => p.id === cur.nextAttemptId);
    if (!next) break;
    chain.push(next);
    cur = next;
  }
  return chain;
}

function nextAttemptInChain(project, projects) {
  if (!project.nextAttemptId) return undefined;
  return projects.find((p) => p.id === project.nextAttemptId);
}

function attemptLabelFor(index) { return `Attempt ${index + 1}`; }

// -- component -------------------------------------------------------

export default function ProjectDetail() {
  const { id } = useParams();
  const [sp] = useSearchParams();
  const location = useLocation();
  const nav = useNavigate();
  const { data, setData, user, refresh, patchProjectParticipation } = useCommons();
  const tour = useTour();

  // Structural back destination for this Detail page.
  //   • If the visitor arrived from a Category page, that page passed
  //     `state.fromCategory: <id>` so back returns to that category's
  //     library.
  //   • Otherwise back returns to Home. Roots keeps its own separate
  //     rules; nothing here should ever fall back to nav(-1).
  //
  // The same `state` object is threaded through the Steward Tools
  // round-trip (Detail → /manage → back-to-Detail) so a visitor who
  // opened the editor from a Category page still returns to that
  // category after cancelling or saving, not to Home.
  const fromCategory = location.state && location.state.fromCategory;
  const backPath = fromCategory ? `/commons/${fromCategory}` : "/";

  // All hooks must be declared unconditionally, above any early returns.
  const [celebrating, setCelebrating] = useState(false);
  const [celebrateMsg, setCelebrateMsg] = useState("");
  const [celebrateVisible, setCelebrateVisible] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  // Shared pending state for every commitment control on this page.
  // `pendingKey` is a string identifying which action+scope is in
  // flight ("pledge:<reqId>", "thing:<reqId>", "withdraw", "decline",
  // "launch"). Buttons check equality to disable + show a subtle
  // pending indicator, and `runMutation` prevents a duplicate request
  // for the same key while one is already in flight.
  const [pendingKey, setPendingKey] = useState(null);
  const celebrateTimerRef = useRef(null);
  const crossedTimerRef = useRef(null);
  useEffect(() => () => {
    if (celebrateTimerRef.current) clearTimeout(celebrateTimerRef.current);
    if (crossedTimerRef.current) clearTimeout(crossedTimerRef.current);
  }, []);

  if (!data || !user) return <div className="c-load">Loading…</div>;
  const project = projectById(data.projects, id);
  if (!project) return <div className="c-load">Not found.</div>;

  const triggerCelebrate = (msg) => {
    setCelebrateMsg(msg);
    setCelebrateVisible(true);
    if (celebrateTimerRef.current) clearTimeout(celebrateTimerRef.current);
    celebrateTimerRef.current = setTimeout(() => setCelebrateVisible(false), 1100);
  };

  const chain = chainInfo(project, data.projects);
  const attemptIndex = chain.findIndex((p) => p.id === project.id);
  const attemptNumber = project.attemptNumber || (attemptIndex + 1);
  const isRetry = attemptNumber > 1;
  const firstAttempt = chain[0];
  const nextAttempt = nextAttemptInChain(project, data.projects);
  // Full timeline (past + future) drives the segmented attempt pager,
  // letting the visitor jump directly to any attempt without walking one
  // hop at a time.
  const attemptsTimeline = fullChainInfo(project, data.projects);

  const steward = personById(data.people, project.stewardId);
  const founder = firstAttempt ? personById(data.people, firstAttempt.stewardId) : steward;
  const coStewards = (project.coStewardIds || [])
    .filter((pid, i, arr) => pid && pid !== project.stewardId && arr.indexOf(pid) === i)
    .map((pid) => personById(data.people, pid))
    .filter(Boolean);

  const iAmSteward = isStewardOrCo(user.personId, project);
  const iAmIn = personIsIn(user.personId, project.id, data.pledges);
  const myPledges = personPledges(user.personId, project.id, data.pledges);
  // A person may hold multiple active People-role pledges on the same
  // project (Commons trusts the person; no auto-withdraw). Track them
  // as a Set so every role card can check membership independently.
  const myPeopleReqIds = new Set(
    myPledges
      .filter((p) => project.requirements.find((r) => r.id === p.requirementId)?.kind === "people")
      .map((p) => p.requirementId)
  );
  const invitedBy = sp.get("invitedBy") || inviterForProject(user.personId, project, data.invitations);
  const inviter = invitedBy ? personById(data.people, invitedBy) : undefined;
  const mutable = commitmentsAreMutable(project);

  // Participation-invariant gates (Mar 2026). Kept in sync with the
  // server-side authorisation gate — see server.py "Participation model".
  const canPledgePeople = mutable && mayPledgePeople(user.personId, project, data.pledges, data.invitations);
  const canPledgeThing = mutable && mayPledgeThing(user.personId, project, data.pledges);
  const canInvite = mayInvite(user.personId, project, data.pledges);
  const isInvited = hasPendingInvitation(user.personId, project, data.invitations);
  const isUninvitedVisitor = !iAmSteward && !inviter && !isInvited;

  const peopleReqs = peopleRequirements(project);
  const thingReqs = thingRequirements(project);
  const peopleCommitted = totalPeopleCommitted(project, data.pledges);
  const peopleNeeded = totalPeopleNeeded(project);
  const peoplePct = peopleNeeded ? Math.round((peopleCommitted / peopleNeeded) * 100) : 0;

  const thingsNeeded = thingReqs.reduce((s, r) => s + r.needed, 0);
  const thingsPledged = thingReqs.reduce((s, r) => s + Math.min(r.needed, pledgedForReq(r.id, data.pledges)), 0);
  const thingsPct = thingsNeeded ? Math.round((thingsPledged / thingsNeeded) * 100) : 0;
  const openThingKinds = thingReqs.filter((r) => pledgedForReq(r.id, data.pledges) < r.needed).length;

  const helpers = uniqueHelpersCount(project.id, data.pledges);
  const peopleShort = Math.max(0, peopleNeeded - peopleCommitted);
  const thingsShort = Math.max(0, thingsNeeded - thingsPledged);

  const active = activePledges(project.id, data.pledges);

  // First unclaimed / open People requirement — used to anchor the
  // guided tour highlight on a stable, meaningful element.
  const firstOpenPeopleReqId =
    peopleReqs.find(
      (r) => pledgedForReq(r.id, data.pledges) < r.needed && !myPeopleReqIds.has(r.id)
    )?.id || peopleReqs[0]?.id;

  // ---- mutations ----

  // Serialize a mutation under `key`. If a request for the same key
  // is already in flight, silently drop the extra tap (prevents the
  // "clicked 3 times because nothing appeared to happen" bug). On
  // failure, clear pending and surface a quiet toast so the user
  // knows they can retry.
  const runMutation = async (key, fn) => {
    if (pendingKey === key) return;
    setPendingKey(key);
    try {
      await fn();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[commons] mutation failed:", key, err);
      toast("Couldn't save that. Try again in a moment.");
    } finally {
      setPendingKey(null);
    }
  };

  const onPledgePerson = (reqId) => runMutation(`pledge:${reqId}`, async () => {
    if (!mutable) return;
    const res = await makePledge(user.personId, project.id, reqId, 1);
    // Option D: server returns the authoritative project + this
    // visitor's post-mutation pledge/invitation slice for the project.
    // We install the slice; every other row in the store is untouched.
    patchProjectParticipation({
      personId: user.personId,
      projectId: project.id,
      project: res.project,
      patch: res.patch,
    });
    if (res.crossedThreshold) {
      setCelebrating(true);
      if (crossedTimerRef.current) clearTimeout(crossedTimerRef.current);
      crossedTimerRef.current = setTimeout(() => setCelebrating(false), 2500);
    } else {
      triggerCelebrate("You're in.");
    }
    // Advance the guided tour if we're at Stop 4.
    tour.markCommitmentMade();
  });
  const onPledgeThing = (reqId, delta) => runMutation(`thing:${reqId}`, async () => {
    if (!mutable) return;
    const existing = myPledges.find((p) => p.requirementId === reqId);
    const cur = existing?.quantity || 0;
    const next = Math.max(0, cur + delta);
    const res = await setQuantity(user.personId, project.id, reqId, next);
    patchProjectParticipation({
      personId: user.personId,
      projectId: project.id,
      project: res.project,
      patch: res.patch,
    });
    if (delta > 0 && cur === 0) triggerCelebrate("You're bringing one.");
  });
  const onWithdraw = (reqId) => runMutation(`withdraw:${reqId || "all"}`, async () => {
    if (!mutable) return;
    const res = await withdraw(user.personId, project.id, reqId);
    patchProjectParticipation({
      personId: user.personId,
      projectId: project.id,
      project: res.project,
      patch: res.patch,
    });
    toast("Stepped back.");
  });
  const onDecline = () => runMutation("decline", async () => {
    const res = await declineInvite(user.personId, project.id);
    patchProjectParticipation({
      personId: user.personId,
      projectId: project.id,
      project: res.project,
      patch: res.patch,
    });
    nav("/");
  });
  const onLaunch = () => runMutation("launch", async () => {
    const res = await launchProject(project.id);
    setData(res.data);
    toast.success("Launched. Now gathering.");
  });

  // ---- Roots preview (steward first, then earliest committers) ----
  const rootsPreview = (() => {
    const stewardIdx = data.people.find((p) => p.id === project.stewardId);
    const seen = new Set([project.stewardId]);
    const ids = [project.stewardId];
    [...active].sort((a, b) => a.timestamp - b.timestamp).forEach((p) => {
      if (!seen.has(p.personId)) { seen.add(p.personId); ids.push(p.personId); }
    });
    const shown = ids.slice(0, 3);
    const overflow = Math.max(0, ids.length - shown.length);
    const chips = shown.map((pid) => {
      const person = personById(data.people, pid);
      return { initial: (person?.name || "?").charAt(0).toUpperCase() };
    });
    return { chips, overflow };
  })();

  // ---- "Who's in" list ----
  const peopleReqIds = new Set(peopleReqs.map((r) => r.id));
  const whosIn = (() => {
    const seenPersons = new Set();
    const order = [];
    // Steward first
    seenPersons.add(project.stewardId);
    order.push(project.stewardId);
    for (const co of project.coStewardIds || []) {
      if (!seenPersons.has(co)) { seenPersons.add(co); order.push(co); }
    }
    [...active].sort((a, b) => a.timestamp - b.timestamp).forEach((p) => {
      if (!seenPersons.has(p.personId)) { seenPersons.add(p.personId); order.push(p.personId); }
    });
    return order;
  })();

  const rolesFor = (personId) => {
    const my = active.filter((p) => p.personId === personId);
    const peopleRoles = my
      .filter((p) => peopleReqIds.has(p.requirementId))
      .map((p) => project.requirements.find((r) => r.id === p.requirementId)?.label)
      .filter(Boolean);
    // Defensive grouping: collapse any Thing pledges that share a
    // requirementId into a single "Bringing N X" line so a person
    // renders one contribution per requirement even if the underlying
    // data contains legacy duplicate rows (the fixed generator no
    // longer produces them; this guards against pre-fix seed data or
    // any future path that appends instead of increments).
    const thingTotals = new Map(); // reqId → summed quantity
    for (const p of my) {
      if (peopleReqIds.has(p.requirementId)) continue;
      thingTotals.set(
        p.requirementId,
        (thingTotals.get(p.requirementId) || 0) + p.quantity,
      );
    }
    const thingRoles = [];
    for (const [reqId, qty] of thingTotals) {
      const r = project.requirements.find((x) => x.id === reqId);
      if (!r || qty <= 0) continue;
      thingRoles.push(
        `Bringing ${qty} ${labelForCount(qty, r.label, r.pluralLabel).toLowerCase()}`,
      );
    }
    return [...peopleRoles, ...thingRoles];
  };

  const status = effectiveStatus(project, data.pledges);
  const isClosed = status === "closed-not-met";
  const isDraft = status === "draft";
  const isHappening = status === "threshold-crossed" || status === "happening";
  const isCompleted = status === "completed";
  const dynLabel = statusLabelDynamic(status, peopleCommitted, peopleNeeded, isRetry);
  const isAlmostThere = dynLabel.startsWith("🔥");

  // "Invite Others" eligibility — a Commons participation action
  // available on every eligible active attempt. The demo does not yet
  // support real person-to-person invitations, so the question of how
  // an outside inviter appears in Roots is deferred; for now this is
  // strictly an affordance change: you don't have to join a project
  // to invite someone else to it.
  //
  // Visible whenever:
  //   • this attempt is currently active (gathering / threshold-crossed
  //     / happening — including dynamically-almost-there), AND
  //   • this is not a historical attempt in a retry chain (has no
  //     `nextAttemptId`).
  // Never shown for drafts, completed projects, closed/failed
  // historical attempts, or cancelled projects.
  const isActiveAttempt = (
    status === "gathering" || status === "threshold-crossed" || status === "happening"
  ) && !project.nextAttemptId;
  // Mar 2026: only active participants (founder / co-steward / active
  // People pledger) may create invitations. Uninvited visitors and
  // pending invitees can browse but not recruit yet — matching the
  // backend authorisation gate in server.py may_invite().
  const canInviteOthers = isActiveAttempt && canInvite;

  const inviterCompletedCount = inviter?.completedProjectsCount || 0;

  const onSendInvite = async ({ inviteeName, inviteePhone, note }) => {
    await inviteToProject(project.id, user.personId, inviteeName, inviteePhone, note);
    await refresh();
    setInviteOpen(false);
    toast.success(`Invitation to ${inviteeName} is ready.`);
  };

  return (
    <div style={{ paddingBottom: 40 }}>
      <div className="c-detail-topbar">
        {/* Structural back — Project Detail is a child of the Home
            browsing experience (or of a Category page, if that's how
            the visitor arrived). Deliberately NOT `nav(-1)`: a
            round-trip through Steward Tools threads state through so
            back never bounces into the editor. Roots' own structural
            rules are unaffected. */}
        <button className="c-detail-back" onClick={() => nav(backPath, { state: { restoreScroll: true } })} data-testid="detail-back-btn">
          <ArrowLeft size={20} />
          <span>Commons</span>
        </button>
        <div />
      </div>

      <div className="c-detail-hero">
        {/* You've been invited hero card */}
        {inviter && !iAmIn && !iAmSteward ? (
          <div className="c-invited-hero" data-testid="invited-hero">
            <div className="c-invited-hero-eyebrow">✉  You've been invited</div>
            <div className="c-invited-hero-title">
              {inviter.name} invited you.{" "}
              {personIsIn(inviter.id, project.id, data.pledges) ? `${inviter.name} has already committed.` : ""}
            </div>
            {inviterCompletedCount > 0 ? (
              <div className="c-invited-hero-sub">
                {inviter.name} has been part of {inviterCompletedCount} completed{" "}
                {inviterCompletedCount === 1 ? "project" : "projects"} on Commons.
              </div>
            ) : null}
          </div>
        ) : null}

        {/* Attempt pager — one row that shows the whole retry story
            (Attempt 1 → Attempt N). Tap any chip to move directly to that
            attempt; the current attempt is highlighted and non-interactive.
            Renders only when the project has more than one attempt. */}
        {attemptsTimeline.length > 1 ? (
          <nav
            className="c-attempt-pager"
            aria-label="Attempts in this project's history"
            data-testid="attempt-pager"
          >
            {attemptsTimeline.map((p, i) => {
              const isCurrent = p.id === project.id;
              const closed = p.status === "closed-not-met";
              const cls = [
                "c-attempt-chip",
                isCurrent ? "c-attempt-chip-current" : "",
                closed && !isCurrent ? "c-attempt-chip-past" : "",
              ].filter(Boolean).join(" ");
              return (
                <button
                  key={p.id}
                  type="button"
                  className={cls}
                  onClick={() => { if (!isCurrent) nav(`/project/${p.id}`); }}
                  disabled={isCurrent}
                  aria-current={isCurrent ? "page" : undefined}
                  data-testid={`attempt-chip-${i + 1}`}
                >
                  <span className="c-attempt-chip-label">Attempt {i + 1}</span>
                </button>
              );
            })}
          </nav>
        ) : null}

        {/* Header: emoji + title horizontally */}
        <div className="c-detail-headline">
          <div className="c-detail-emoji">{project.emoji}</div>
          <h1 className="c-detail-title" data-testid="detail-title">{project.title}</h1>
        </div>

        {project.attribution ? (
          <div
            className="c-detail-attribution"
            data-testid="detail-attribution"
          >
            Organized by <strong>{project.attribution}</strong>
          </div>
        ) : null}

        {/* Status pill — muted default, gold when Almost There, living
            sage when happening, archival olive when completed. Two greens,
            one alive, one settled. */}
        <div className={`c-status-outline ${isAlmostThere ? "c-status-almost" : ""} ${isHappening ? "c-status-happening" : ""} ${isCompleted ? "c-status-completed" : ""}`}>
          {isClosed ? "Didn't Gather" : dynLabel}
        </div>

        {/* Inviter chip */}
        {inviter ? (
          <div className="c-provenance" style={{ marginTop: 12 }}>
            🌱 Invited by <strong style={{ marginLeft: 4 }}>{inviter.name}</strong>
          </div>
        ) : null}

        <p className="c-detail-purpose" style={{ marginTop: 12 }}>{project.purpose}</p>

        {/* Meta rows with Lucide icons */}
        <div className="c-meta-list">
          {project.commitBy ? (
            <div className="c-meta-icon-row">
              <Calendar size={16} className="c-meta-lucide" />
              <span className="muted">Commit by:</span>
              <strong>{project.commitBy}</strong>
            </div>
          ) : null}
          <div className="c-meta-icon-row">
            <Clock size={16} className="c-meta-lucide" />
            <span className="muted">Happens:</span>
            <strong>{project.when}</strong>
          </div>
          {project.where ? (
            <div className="c-meta-icon-row">
              <MapPin size={16} className="c-meta-lucide" />
              <strong>{project.where}</strong>
            </div>
          ) : null}
          <div className="c-meta-icon-row">
            <User size={16} className="c-meta-lucide" />
            <span className="muted">
              {coStewards.length ? `Stewards (${coStewards.length + 1}):` : "Steward:"}
            </span>
            <strong>
              {steward?.name}
              {project.stewardTitle ? ` (${project.stewardTitle})` : ""}
              {isRetry && founder && founder.id === project.stewardId && !project.stewardTitle ? " (Founder)" : ""}
              {isRetry && founder && founder.id !== project.stewardId ? `, ${founder.name} (Founder)` : ""}
              {coStewards.length ? `, ${coStewards.map((p) => p.name).join(", ")}` : ""}
            </strong>
          </div>
        </div>

        {/* Steward Tools — the per-project management entry point,
            visible only to stewards and co-stewards of THIS project.
            Positioned immediately after the steward-metadata row so
            the affordance reads as "attached to this project, right
            where the page confirms I'm the one running it". Opens the
            edit mode of the same project-definition form that powers
            Create. Non-stewards see nothing here. */}
        {iAmSteward ? (
          <button
            type="button"
            className="c-steward-tools-btn"
            onClick={() => nav(`/project/${project.id}/manage`, { state: location.state || undefined })}
            data-testid="steward-tools-btn"
            aria-label={`Open steward tools for ${project.title}`}
          >
            <Wrench size={14} strokeWidth={2.5} />
            <span>Steward Tools</span>
            <ChevronRight size={15} />
          </button>
        ) : null}

        {/* Combined aggregate card — People + Essential Things in one container */}
        {status !== "draft" ? (
          <div className="c-agg-card" data-tour="readiness">
            {/* TODO(cruise-demo): Remove this Cruise-Ship-specific
                threshold presentation once requirement-level threshold
                summaries have a general design. The generic aggregate
                (`peopleCommitted of peopleNeeded`) treats 3,000
                Travelers + 50 DJs + 30 tarot readers + 1 maritime-savvy
                human as 3,081 interchangeable role-slots — which
                collapses qualitatively different requirements into a
                single unhelpful number. For this one project only we
                lead with the defining 3,000-Traveler threshold and
                enumerate the other five requirements deadpan. All
                numbers below are read dynamically from the project's
                own requirements + pledges — no hard-coded values. */}
            {isCruiseShipProject(project) ? (() => {
              const head = cruiseTravelerHeadline(project, data.pledges);
              const travelerReq = head?.requirement;
              const travelerFilled = head?.filled ?? 0;
              const travelerNeeded = head?.needed ?? 0;
              const fmt = (n) => n.toLocaleString("en-US");
              const renderRow = (req) => {
                const got = Math.min(req.needed, pledgedForReq(req.id, data.pledges));
                const label = req.needed === 1
                  ? req.label
                  : (req.pluralLabel || `${req.label}s`);
                return (
                  <li key={req.id} className="c-agg-req-row">
                    <span className="c-agg-req-count">{fmt(got)} / {fmt(req.needed)}</span>
                    <span className="c-agg-req-label">{label}</span>
                  </li>
                );
              };
              const otherPeopleReqs = peopleReqs.filter((r) => r.id !== `${CRUISE_ID}-r1`);
              return (
                <>
                  <div className="c-agg-section">
                    <div className="c-agg-eyebrow">People</div>
                    <div>
                      <span className="c-agg-count">{fmt(travelerFilled)}</span>
                      <span className="c-agg-of">of {fmt(travelerNeeded)} people are down</span>
                    </div>
                    <div className="c-agg-bar-track">
                      <div
                        className="c-agg-bar-fill"
                        style={{
                          width: `${travelerNeeded ? Math.round((travelerFilled / travelerNeeded) * 100) : 0}%`,
                          background: "var(--c-brand)",
                        }}
                      />
                    </div>
                    <ul className="c-agg-req-list" data-testid="cruise-people-req-list">
                      {renderRow(travelerReq)}
                      {otherPeopleReqs.map(renderRow)}
                    </ul>
                  </div>

                  {thingReqs.length > 0 ? (
                    <div className="c-agg-section">
                      <div className="c-agg-eyebrow">Things</div>
                      <ul className="c-agg-req-list" data-testid="cruise-things-req-list">
                        {thingReqs.map(renderRow)}
                      </ul>
                    </div>
                  ) : null}

                  {status === "gathering" ? (
                    <div className="c-launch-copy">
                      This project launches when all the roles and essential things are covered.
                    </div>
                  ) : null}
                </>
              );
            })() : (
            <>
            <div className="c-agg-section">
              <div className="c-agg-eyebrow">People</div>
              <div>
                <span className="c-agg-count">{peopleCommitted}</span>
                <span className="c-agg-of">of {peopleNeeded} roles filled</span>
              </div>
              <div className="c-agg-bar-track">
                <div className="c-agg-bar-fill" style={{ width: `${peoplePct}%`, background: "var(--c-brand)" }} />
              </div>
            </div>

            {thingReqs.length > 0 ? (
              <div className="c-agg-section">
                <div className="c-agg-eyebrow">Things</div>
                <div>
                  <span className="c-agg-count sage">{thingsPledged}</span>
                  <span className="c-agg-of">of {thingsNeeded} pledged</span>
                </div>
                <div className="c-agg-bar-track">
                  <div className="c-agg-bar-fill" style={{ width: `${thingsPct}%`, background: "var(--c-brand-secondary)" }} />
                </div>
              </div>
            ) : null}

            {(status === "gathering") ? (
              <>
                <div className="c-launch-copy">
                  This project launches when all the roles and essential things are covered.
                </div>
                {(peopleShort > 0 || thingsShort > 0) ? (
                  <div className="c-launch-italic">
                    {peopleShort > 0 ? `${peopleShort} ${peopleShort === 1 ? "role" : "roles"}` : ""}
                    {peopleShort > 0 && thingsShort > 0 ? " and " : ""}
                    {thingsShort > 0 ? `${thingsShort} essential ${thingsShort === 1 ? "thing" : "things"}` : ""}
                    {" "}still needed.
                  </div>
                ) : null}
              </>
            ) : null}
            </>
            )}

            {(isHappening || isCompleted) ? (
              <div className={`c-happening-note ${isCompleted ? "completed" : ""}`}>
                {isCompleted
                  ? `${helpers} of us made this happen. A moment kept in the community's memory.`
                  : `It's happening. ${helpers} of us brought this to life.`}
              </div>
            ) : null}
          </div>
        ) : null}

        {/* Roots pill — placed BELOW the aggregate card, canonical order */}
        {helpers > 0 ? (
          <button
            className="c-roots-inline"
            onClick={() => nav(`/roots/${project.id}`)}
            data-testid="roots-inline-pill"
          >
            <span>🌱</span>
            <span>Roots</span>
            <span className="c-roots-avatars">
              {rootsPreview.chips.map((c, i) => <span key={i} className="c-avatar">{c.initial}</span>)}
            </span>
            {rootsPreview.overflow > 0 ? (
              <span className="c-roots-plus">+{rootsPreview.overflow}</span>
            ) : null}
            <ChevronRight size={16} color="var(--c-on-surface-2)" style={{ marginLeft: 4 }} />
          </button>
        ) : null}

        {/* Invite Others — a Commons participation action available to
            anyone already in the project while an active attempt is
            underway. Terracotta CTA to visually separate it from the
            sage steward-only chip above and the sage roots pill above.
            Opens a bottom sheet that creates a pending, phone-anchored
            invitation attributed to the current user. Invitees appear
            in Roots only after they onboard with this phone and
            pledge — preserving Roots' semantic. */}
        {canInviteOthers ? (
          <button
            type="button"
            className="c-invite-others-btn"
            onClick={() => setInviteOpen(true)}
            data-testid="invite-others-btn"
            aria-label={`Invite someone to ${project.title}`}
          >
            <Mail size={15} strokeWidth={2} />
            <span>Invite Others</span>
          </button>
        ) : null}
      </div>

      <div style={{ padding: "0 20px" }}>
        {isDraft && iAmSteward ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 16 }}>
            <div className="c-body">This project is still a draft — invite co-stewards, then launch when ready.</div>
            <button className="c-btn c-btn-primary" onClick={onLaunch} disabled={pendingKey === "launch"} aria-busy={pendingKey === "launch"} data-testid="launch-btn">Launch — start gathering</button>
          </div>
        ) : null}

        {peopleReqs.length > 0 ? (
          <>
            <div className="c-section-eyebrow" style={{ marginTop: 36, marginBottom: 6 }}>People</div>
            <div className="c-section-hint" data-testid="roles-multirole-hint">You can be in for more than one role.</div>
            <div className="c-req-list">
              {peopleReqs.map((r) => {
                const got = pledgedForReq(r.id, data.pledges);
                const met = got >= r.needed;
                const mine = myPeopleReqIds.has(r.id);
                const short = Math.max(0, r.needed - got);
                const countLine = met
                  ? `${got} of ${r.needed} filled · fully covered`
                  : `${got} of ${r.needed} filled · ${short} still needed`;
                const mineLine = mine && short > 0
                  ? `${short} still needed.`
                  : null;
                return (
                  <div
                    key={r.id}
                    className={`c-req-row ${met ? "met" : ""}`}
                    data-testid={`req-${r.id}`}
                    data-tour={r.id === firstOpenPeopleReqId ? "volunteer-role" : undefined}
                  >
                    <div className="c-req-body">
                      <div className="c-req-title">{labelForCount(r.needed, r.label, r.pluralLabel)}</div>
                      <RequirementDescription shortDescription={r.shortDescription} description={r.description} testIdPrefix={`req-${r.id}`} />
                      <div className="c-req-progress">{countLine}</div>
                      {mineLine ? <div className="c-req-progress c-req-mine">{mineLine}</div> : null}
                      <div className="c-req-action">
                        {mine ? (
                          <button
                            className={`c-im-in-btn done ${pendingKey === `withdraw:${r.id}` ? "is-pending" : ""}`}
                            onClick={() => onWithdraw(r.id)}
                            disabled={pendingKey === `withdraw:${r.id}`}
                            aria-busy={pendingKey === `withdraw:${r.id}`}
                            data-testid={`req-${r.id}-withdraw`}
                            aria-label="You're in. Tap to step back."
                            title="You're in — tap to step back"
                          >
                            <Check size={15} strokeWidth={3} />
                            <span>I'm in</span>
                            {pendingKey === `withdraw:${r.id}` ? <span className="c-im-in-spinner" aria-hidden="true" /> : null}
                          </button>
                        ) : met ? (
                          <span className="c-req-status-covered" aria-label="This role is fully covered">
                            <Check size={14} strokeWidth={2.6} />
                            <span>Covered</span>
                          </span>
                        ) : mutable && canPledgePeople ? (
                          <button
                            className={`c-im-in-btn ${pendingKey === `pledge:${r.id}` ? "is-pending" : ""}`}
                            onClick={() => onPledgePerson(r.id)}
                            disabled={pendingKey === `pledge:${r.id}`}
                            aria-busy={pendingKey === `pledge:${r.id}`}
                            data-testid={`req-${r.id}-pledge`}
                            aria-label={`Join as a ${r.label}`}
                          >
                            <span>Join</span>
                            {pendingKey === `pledge:${r.id}` ? <span className="c-im-in-spinner" aria-hidden="true" /> : null}
                          </button>
                        ) : mutable ? (
                          <span
                            className="c-req-invite-only"
                            data-testid={`req-${r.id}-invite-only`}
                            aria-label="This role is available by invitation"
                          >
                            Joining happens by invitation.
                          </span>
                        ) : (
                          <span className="c-req-status-covered" aria-label="Not open yet">
                            <Check size={14} strokeWidth={2.6} />
                            <span>Not open</span>
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        ) : null}

        {thingReqs.length > 0 ? (
          <>
            <div className="c-section-eyebrow" style={{ marginTop: 36, marginBottom: 12 }}>Things</div>
            <div className="c-req-list">
              {thingReqs.map((r) => {
                const got = pledgedForReq(r.id, data.pledges);
                const met = got >= r.needed;
                const mine = myPledges.find((p) => p.requirementId === r.id)?.quantity || 0;
                const short = Math.max(0, r.needed - got);
                const countLine = met
                  ? `${got} of ${r.needed} pledged · fully covered`
                  : `${got} of ${r.needed} pledged · ${short} still needed`;
                const mineLine = mine > 0
                  ? `You're bringing ${mine} ${(mine === 1 ? r.label : (r.pluralLabel || r.label)).toLowerCase()}.`
                  : null;
                return (
                  <div
                    key={r.id}
                    className={`c-req-row c-req-row--thing ${met ? "met" : ""}`}
                    data-testid={`req-${r.id}`}
                  >
                    <div className="c-req-emoji">{r.emoji || "📦"}</div>
                    <div className="c-req-body">
                      <div className="c-req-title">{labelForCount(r.needed, r.label, r.pluralLabel)}</div>
                      <RequirementDescription shortDescription={r.shortDescription} description={r.description} testIdPrefix={`req-${r.id}`} />
                      <div className="c-req-progress">{countLine}</div>
                      {mineLine ? <div className="c-req-progress c-req-mine">{mineLine}</div> : null}
                      <div className="c-req-action">
                        {mine > 0 && mutable ? (
                          <div className={`c-req-stepper ${pendingKey === `thing:${r.id}` ? "is-pending" : ""}`} aria-busy={pendingKey === `thing:${r.id}`}>
                            <button onClick={() => onPledgeThing(r.id, -1)} disabled={pendingKey === `thing:${r.id}`} className="c-req-stepper-btn c-req-stepper-btn--minus" data-testid={`req-${r.id}-minus`} aria-label="Reduce"><Minus size={20} strokeWidth={2.8} /></button>
                            <span className="c-req-stepper-qty">{mine}</span>
                            <button onClick={() => onPledgeThing(r.id, +1)} disabled={pendingKey === `thing:${r.id}`} className="c-req-stepper-btn c-req-stepper-btn--plus" data-testid={`req-${r.id}-plus`} aria-label="Add one"><Plus size={22} strokeWidth={3} /></button>
                          </div>
                        ) : met ? (
                          <span className="c-req-status-covered" aria-label="This one is fully covered">
                            <Check size={14} strokeWidth={2.6} />
                            <span>Covered</span>
                          </span>
                        ) : mutable && canPledgeThing ? (
                          <button
                            className={`c-im-in-btn ${pendingKey === `thing:${r.id}` ? "is-pending" : ""}`}
                            onClick={() => onPledgeThing(r.id, +1)}
                            disabled={pendingKey === `thing:${r.id}`}
                            aria-busy={pendingKey === `thing:${r.id}`}
                            data-testid={`req-${r.id}-plus`}
                            aria-label={`I'll bring one ${r.label.toLowerCase()}`}
                          >
                            <span>I'll bring one</span>
                            {pendingKey === `thing:${r.id}` ? <span className="c-im-in-spinner" aria-hidden="true" /> : null}
                          </button>
                        ) : mutable ? (
                          <span
                            className="c-req-invite-only"
                            data-testid={`req-${r.id}-invite-only`}
                            aria-label="Available to project participants"
                          >
                            Joining happens by invitation.
                          </span>
                        ) : (
                          <span className="c-req-status-covered" aria-label="Not open yet">
                            <Check size={14} strokeWidth={2.6} />
                            <span>Not open</span>
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        ) : null}

        {inviter && !iAmIn ? (
          <button className="c-btn c-btn-ghost" style={{ width: "100%", marginTop: 20 }} onClick={onDecline} disabled={pendingKey === "decline"} aria-busy={pendingKey === "decline"} data-testid="decline-btn">
            Not this time
          </button>
        ) : null}

        {status === "completed" ? (
          <button className="c-btn c-btn-inverse" style={{ width: "100%", marginTop: 12 }} onClick={() => nav(`/story/${project.id}`)} data-testid="see-story-btn">
            Read the story
          </button>
        ) : null}

        {/* Who's in list */}
        {whosIn.length > 0 && !isDraft ? (
          <>
            <div className="c-whos-in-eyebrow">Committed</div>
            <h3 className="c-whos-in-title">Who's in</h3>
            <div>
              {whosIn.map((pid) => {
                const person = personById(data.people, pid);
                if (!person) return null;
                const isTheSteward = pid === project.stewardId;
                const isCo = (project.coStewardIds || []).includes(pid);
                const invId = inviterForProject(pid, project, data.invitations);
                const inv = invId ? personById(data.people, invId) : null;
                const roles = rolesFor(pid);
                const rolesText = roles.length ? roles.join(" · ") : "Committed";
                let sub = "";
                if (isTheSteward) {
                  const count = person.completedProjectsCount || 0;
                  sub = count > 0 ? `Steward · ${count} completed ${count === 1 ? "project" : "projects"}` : "Steward";
                } else if (isCo) {
                  sub = "Co-steward";
                } else if (inv) {
                  sub = `Invited by ${inv.name}`;
                }
                return (
                  <div key={pid} className="c-whos-in-row" data-testid={`whos-in-${pid}`}>
                    <div className="c-whos-in-avatar">{person.name.charAt(0).toUpperCase()}</div>
                    <div className="c-whos-in-body">
                      <div className="c-whos-in-name-line">
                        <strong>{person.name}</strong>
                        {rolesText ? <span className="c-whos-in-roles">{" · "}{rolesText}</span> : null}
                      </div>
                      {sub ? <div className="c-whos-in-sub">{sub}</div> : null}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        ) : null}
      </div>

      {celebrating ? (
        <div className="c-modal-scrim">
          <div className="c-modal text-center">
            <div className="c-celebrate-emoji">🎉</div>
            <div className="c-h2">It's happening!</div>
            <div className="c-body">Everyone who committed just got notified. This project has enough people and things to happen.</div>
            <button className="c-btn c-btn-primary" onClick={() => setCelebrating(false)}>Wonderful</button>
          </div>
        </div>
      ) : null}
      <Celebration visible={celebrateVisible} message={celebrateMsg} />
      <InviteSheet
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        onSend={onSendInvite}
        projectTitle={project.title}
      />
    </div>
  );
}
